import { useState, useMemo, useCallback, useEffect } from 'react';

export interface UsePaginationOptions {
  data: any[];
  defaultPageSize?: number;
  storageKey?: string;
}

export interface UsePaginationReturn<T> {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  paginatedData: T[];
  totalItems: number;
  startIndex: number;
  endIndex: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

const DEFAULT_PAGE_SIZE = 10;
const VALID_PAGE_SIZES = [5, 10, 20, 50, 100] as const;

export function usePagination<T = any>({
  data,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  storageKey,
}: UsePaginationOptions): UsePaginationReturn<T> {
  // ページサイズをローカルストレージから復元（storageKeyが指定されている場合）
  const getInitialPageSize = useCallback(() => {
    if (storageKey) {
      const stored = localStorage.getItem(`pagination_${storageKey}_pageSize`);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (VALID_PAGE_SIZES.includes(parsed as any)) {
          return parsed;
        }
      }
    }
    return defaultPageSize;
  }, [storageKey, defaultPageSize]);

  const [pageSize, setPageSizeState] = useState(getInitialPageSize);
  const [currentPage, setCurrentPageState] = useState(1);

  // データが変更されたときに現在のページを調整
  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // 現在のページが総ページ数を超えている場合は調整
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPageState(Math.max(1, totalPages));
    }
  }, [currentPage, totalPages]);

  // ページネートされたデータを計算
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  // 表示範囲のインデックス
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalItems);

  // ページング操作のハンドラー
  const setCurrentPage = useCallback(
    (page: number) => {
      const validPage = Math.max(1, Math.min(page, totalPages));
      setCurrentPageState(validPage);
    },
    [totalPages]
  );

  const setPageSize = useCallback(
    (size: number) => {
      if (!VALID_PAGE_SIZES.includes(size as any)) {
        console.warn(`Invalid page size: ${size}. Valid sizes are: ${VALID_PAGE_SIZES.join(', ')}`);
        return;
      }

      setPageSizeState(size);

      // ローカルストレージに保存（storageKeyが指定されている場合）
      if (storageKey) {
        localStorage.setItem(`pagination_${storageKey}_pageSize`, size.toString());
      }

      // 現在のページを調整（新しいページサイズで有効な範囲内に）
      const newTotalPages = Math.max(1, Math.ceil(totalItems / size));
      if (currentPage > newTotalPages) {
        setCurrentPageState(Math.max(1, newTotalPages));
      }
    },
    [storageKey, totalItems, currentPage]
  );

  const nextPage = useCallback(() => {
    setCurrentPage(currentPage + 1);
  }, [currentPage, setCurrentPage]);

  const previousPage = useCallback(() => {
    setCurrentPage(currentPage - 1);
  }, [currentPage, setCurrentPage]);

  const canGoNext = currentPage < totalPages;
  const canGoPrevious = currentPage > 1;

  return {
    currentPage,
    totalPages,
    pageSize,
    paginatedData,
    totalItems,
    startIndex,
    endIndex,
    setCurrentPage,
    setPageSize,
    nextPage,
    previousPage,
    canGoNext,
    canGoPrevious,
  };
}

// 有効なページサイズの定数をエクスポート
export { VALID_PAGE_SIZES };
